/**
 * 「다른 기사 대신 신청」(#332 이현숙 — 서류신청처럼 본인 외 다른 기사 것도 · 본인이 직접 못 할 때 다른 기사님이 대신 입력).
 * 세금계산서·페이앱·카드·현금영수증·다수배분 신청 화면 위쪽에 접힌 칸으로 둔다(평소엔 버튼 한 줄 — 한 화면에 한 질문).
 * 고를 수 있는 기사 = 지금 운행 편성이 있는 기사(배분 기사 찾기와 같은 목록 · 서버가 신청 때 다시 본다).
 * 고르면 신청 칸(법인·신청자)을 그 기사 기준으로 다시 불러오고, 내역에는 「대신 신청: 내 이름」이 남는다.
 */

import { useEffect, useState } from 'react';
import { driverApi } from '../lib/driverApi';
import { useDriverAuth } from '../lib/driverAuth';

export interface ProxyTarget { driverId: number; label: string }

/** 「이름 | 차량」 → 이름. */
export const proxyName = (t: ProxyTarget) => t.label.split(' | ')[0] || t.label;

export default function ProxyPicker({ value, onChange, disabled = false }: {
  value: ProxyTarget | null;
  onChange: (t: ProxyTarget | null) => void;
  disabled?: boolean;
}) {
  const { driver } = useDriverAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<ProxyTarget[] | null>(null);

  // 치는 대로 찾기(두 글자부터 · 멈추면 한 번)
  useEffect(() => {
    if (!open || q.trim().length < 2) { setRows(null); return; }
    const t = setTimeout(() => {
      driverApi.searchPeers({ q: q.trim(), internal: true })
        .then((r) => setRows(r.rows.filter((x) => x.driverId !== driver?.driver_id)))
        .catch(() => setRows([]));
    }, 300);
    return () => clearTimeout(t);
  }, [q, open, driver?.driver_id]);

  if (value) {
    const name = proxyName(value);
    return (
      <div className="mb-6 rounded-2xl bg-amber-50 px-4 py-4 ring-1 ring-amber-200">
        <p className="break-keep text-lg font-bold text-amber-900">{name} 기사님 대신 신청 중</p>
        <p className="mt-1 break-keep text-base text-amber-800">
          신청자가 {name} 기사님으로 들어가고, 내역에 대신 신청한 사람({driver?.driver_name ?? '나'})이 함께 남습니다.
        </p>
        <button
          className="mt-3 min-h-touch w-full rounded-xl border-2 border-amber-300 bg-surface text-lg font-semibold text-amber-900"
          disabled={disabled}
          onClick={() => { onChange(null); setOpen(false); setQ(''); }}
        >
          취소 — 내 이름으로 신청
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        className="mb-6 min-h-touch w-full rounded-xl border-2 border-line bg-surface text-lg text-ink-soft"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        다른 기사 대신 신청하기
      </button>
    );
  }

  return (
    <div className="mb-6 rounded-2xl bg-canvas px-4 py-4 ring-1 ring-line/70">
      <p className="mb-2 break-keep text-base font-semibold text-ink-soft">누구 대신 신청할까요?</p>
      <input
        className="driver-input"
        placeholder="기사 이름 두 글자 이상"
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {rows && rows.length > 0 && (
        <ul className="driver-card mt-2 divide-y">
          {rows.map((r) => (
            <li key={r.driverId}>
              <button className="min-h-touch w-full px-4 py-2 text-left text-lg" onClick={() => { onChange(r); setOpen(false); setQ(''); }}>
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {rows && rows.length === 0 && (
        <p className="mt-2 break-keep text-base text-ink-soft">찾는 기사가 없습니다. 지금 운행 편성이 있는 기사만 나옵니다.</p>
      )}
      <button className="mt-3 min-h-touch w-full rounded-xl border-2 border-line text-lg text-ink-soft" onClick={() => { setOpen(false); setQ(''); }}>
        닫기 — 내 이름으로 신청
      </button>
    </div>
  );
}
